import React from 'react'
import { CircularProgress, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons'
import { EllipsisText } from '../../EllipsisText'
import { GradientFaIcon } from '../../GradientFaIcon'
import { useStyles } from './ChatRecommendations.styles'

/** One row of the list, and the prompt sent when it is picked. */
export interface ChatRecommendation {
  id: string
  text: string
  prompt: string
}

/** Products that can contribute recommendations to the chat. */
export type ChatRecommendationProduct = 'cosell' | 'funding'

/** Keyed by product so a host can read or replace one slice directly. */
export type ChatRecommendationsByProduct = Partial<
  Record<ChatRecommendationProduct, ChatRecommendation[]>
>

// TODO(copy): product wording is not in Figma 345-2700 — confirm.
const PRODUCT_LABELS: Record<ChatRecommendationProduct, string> = {
  cosell: 'Co-sell',
  funding: 'Funding management',
}

/** Fixed order, so the list does not reshuffle as products come and go. */
const PRODUCT_ORDER: ChatRecommendationProduct[] = ['cosell', 'funding']

interface ChatRecommendationsProps {
  title: string
  /** Row icon — sparkles for recommendations, a clipboard for to-dos. */
  icon: IconDefinition
  /** Fills the icon with the Labra AI gradient (an SVG glyph cannot take a
   *  `background-clip: text` one). */
  iconGradient?: boolean
  /** Label for the load-more link, e.g. "More recommendations". */
  moreLabel: string
  recommendations: ChatRecommendationsByProduct
  onBack: () => void
  onSelect: (prompt: string) => void
  /** Omit when everything is already listed; the link is hidden then. */
  onLoadMore?: () => void
  /** True while the next page is in flight, so the link reads as busy. */
  isLoadingMore?: boolean
}

export const ChatRecommendations: React.FC<ChatRecommendationsProps> = ({
  title,
  icon,
  iconGradient = false,
  moreLabel,
  recommendations,
  onBack,
  onSelect,
  onLoadMore,
  isLoadingMore = false,
}) => {
  const { classes } = useStyles()
  const populated = PRODUCT_ORDER.filter(
    product => recommendations[product]?.length
  )

  return (
    <div className={classes.root}>
      <div className={classes.titleRow}>
        <button
          className={classes.backButton}
          type="button"
          onClick={onBack}
          aria-label="Back to chat"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <Typography className={classes.heading}>{title}</Typography>
      </div>

      <div className={classes.scrollArea}>
        {populated.length === 0 ? (
          // TODO(copy): empty wording is not in Figma 345-2700 — confirm.
          <Typography className={classes.empty}>
            Nothing to catch up on right now.
          </Typography>
        ) : (
          populated.map(product => (
            <div key={product} className={classes.group}>
              <Typography className={classes.groupLabel}>
                {PRODUCT_LABELS[product]}
              </Typography>
              <div className={classes.list}>
                {recommendations[product]?.map(recommendation => (
                  <button
                    key={recommendation.id}
                    className={classes.row}
                    type="button"
                    onClick={() => onSelect(recommendation.prompt)}
                  >
                    <span className={classes.rowIcon}>
                      {iconGradient ? (
                        <GradientFaIcon icon={icon} />
                      ) : (
                        <FontAwesomeIcon icon={icon} />
                      )}
                    </span>
                    <EllipsisText
                      text={recommendation.text}
                      className={classes.rowText}
                    />
                    <span
                      className={`${classes.rowChevron} chatRecommendationChevron`}
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {onLoadMore && (
        <button
          className={classes.moreButton}
          type="button"
          onClick={onLoadMore}
          disabled={isLoadingMore}
        >
          {isLoadingMore ? (
            <CircularProgress size={12} thickness={5} />
          ) : (
            moreLabel
          )}
        </button>
      )}
    </div>
  )
}
